import React from 'react';

import { useTranslation } from 'react-i18next';

import { Button, Table } from 'design-system-web';
import { SortDirectionEnum } from 'constants-activate';
import { M, S } from 'components/typography';

import styles from './styles.scss';

type PermissionDetail = { key: string; description: string };
type PermissionCategory = { key: string; value: PermissionDetail[] };

type Props = {
  permissionCategories: PermissionCategory[];
  selectedPermissions: string[];
  teamCount: number;
  canAdvance: boolean;
  onSelect: (permissions: string[]) => void;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
};

export function PermissionSelectionStep({
  permissionCategories,
  selectedPermissions,
  teamCount,
  canAdvance,
  onSelect,
  onNext,
  onBack,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const rows = permissionCategories.flatMap(category =>
    category.value.map(detail => ({
      key: detail.key,
      name: t(detail.description),
      category: category.key,
    }))
  );

  return (
    <div>
      <M mb={2}>
        {t(
          'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.permissionsStep.description',
          { teamCount }
        )}
      </M>

      <Table
        isSelectable
        columns={[
          {
            key: 'name',
            header: t(
              'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.permissionsStep.column.name'
            ),
          },
          {
            key: 'category',
            header: t(
              'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.permissionsStep.column.category'
            ),
          },
        ]}
        data={rows}
        isLoading={false}
        totalRows={rows.length}
        pagination="client"
        hasKeywordFilter
        sorting={{ id: 'category', direction: SortDirectionEnum.Asc }}
        rowSelectionExternal
        rowSelection={selectedPermissions}
        getRowId={(row: { key: string }) => row.key}
        onSelectionChange={onSelect}
        tableKey="bulkTeamManagement.wizardPermissionsTable"
      />
      <S>
        {t(
          'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.selectedCount',
          {
            count: selectedPermissions.length,
            total: rows.length,
          }
        )}
      </S>

      <div className={styles.footer}>
        <Button
          variant="secondary"
          onClick={onClose}
          testId="wizardCloseButton"
        >
          {t(
            'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.close'
          )}
        </Button>
        <Button variant="secondary" onClick={onBack} testId="wizardBackButton">
          {t(
            'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.back'
          )}
        </Button>
        <Button
          disabled={!canAdvance}
          onClick={onNext}
          testId="wizardContinueButton"
        >
          {t(
            'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.continue'
          )}
        </Button>
      </div>
    </div>
  );
}
